import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';
import { currencyService, retailerSettingsService } from '../../services';
import s from './returnSummary.module.css';
import { rawString } from '../../react-components/Language';

const mapStateToProps = ({
  language,
  page: { productType, giftOrder = false } = {},
  returnsSettings: {
    [productType]: {
      useHandlingFeeAsRepairCost,
      refunds: { enable_api: refundApiEnabled = false } = {},
    } = {},
  } = {},
  returnMethods: { selectedMethodKey, selectedMethod: { id: selectedMethodId } = {} } = {},
  returnOptions: {
    selectedOptionMethodKey,
    returnOptionMethods: {
      [selectedOptionMethodKey]: { return_cost: { service_fee: serviceFee } = {} } = {},
    } = {},
  } = {},
  storeLocator: { selectedLocation: { brand_display_name: brandDisplayName } = {} } = {},
}) => ({
  language,
  serviceFee,
  giftOrder,
  refundApiEnabled,
  useHandlingFeeAsRepairCost,
  methodMessage: brandDisplayName
    ? (language[`returns.step_3.${selectedMethodKey}`] || '').replace('$brand', brandDisplayName)
    : language[`returns.step_3.${selectedMethodKey}`],
  freeLabel: rawString({
    langKey: [
      `returns.return_options.${selectedMethodId}.free_label`,
      'returns.general.free_label',
    ],
  }),
});

const FeeSummaryComponent = ({
  returnSubtotal,
  returnCredit,
  restockingFee,
  netCost,
  recyclingFeeReversal,
  returnTax,
  returnCost,
  discountReversal,
  expressUpgradeFee,
  handlingFee,
  creditReversalAmount,
  serviceFee,
  methodMessage,
  onlyBlindItems,
  useHandlingFeeAsRepairCost,
  giftOrder,
  refundApiEnabled,
  estimatedRefund,
  language,
  freeLabel,
}) => {
  const product = retailerSettingsService.getProduct();
  if (refundApiEnabled) return null;
  const hideMethodFees =
    serviceFee > 0 &&
    !returnCost &&
    !netCost &&
    !discountReversal &&
    !expressUpgradeFee &&
    !handlingFee &&
    !creditReversalAmount;
  const hideSummary =
    !netCost && !discountReversal && !methodMessage && !expressUpgradeFee && !handlingFee;

  return (
    <>
      {Boolean(returnSubtotal || returnCredit || restockingFee || netCost || returnTax) && (
        <div className={cx(s.summary_section_container, 'return-summary-fees-container')}>
          {typeof returnSubtotal !== 'undefined' && (
            <div className="summary_fee">
              <p className={cx('return-summary-subtotal-key', s.summary_key)}>
                {language['returns.step_3.subtotal_label']}
              </p>
              <p className={cx('return-summary-subtotal-value', s.summary_value)}>
                {onlyBlindItems && product === 'returns'
                  ? language['returns.general.to_be_determined']
                  : currencyService.formatPrice(returnSubtotal)}
              </p>
              <div className="clearfix" />
            </div>
          )}
          {Boolean(returnCredit) && (
            <div className="summary_fee">
              <p
                className={cx('return-summary-return-credit-key', s.summary_key)}
                dangerouslySetInnerHTML={{
                  __html: language['returns.step_3.return_credit_label'],
                }}
              />
              <p className={cx('return-summary-return-credit-value', s.summary_value)}>
                {currencyService.formatPrice(returnCredit)}
              </p>
              <div className="clearfix" />
            </div>
          )}
          {Boolean(restockingFee) && (
            <div className="summary_fee">
              <p
                className={cx('return-summary-restocking-fee-key', s.summary_key)}
                dangerouslySetInnerHTML={{
                  __html: language['returns.step_3.restocking_fee_label'],
                }}
              />
              <p className={cx('return-summary-restocking-fee-value', s.summary_value)}>
                -{currencyService.formatPrice(restockingFee)}
              </p>
              <div className="clearfix" />
            </div>
          )}
          {Boolean(recyclingFeeReversal) && (
            <div className="summary_fee">
              <p
                className={cx('return-summary-recycling-fee-key', s.summary_key)}
                dangerouslySetInnerHTML={{
                  __html: language['returns.step_3.recycling_fee_label'],
                }}
              />
              <p className={cx('return-summary-recycling-fee-value', s.summary_value)}>
                {currencyService.formatPrice(recyclingFeeReversal)}
              </p>
              <div className="clearfix" />
            </div>
          )}
          {Boolean(returnTax) && (
            <div className="summary_fee">
              <p
                className={cx('return-summary-return-tax-key', s.summary_key)}
                dangerouslySetInnerHTML={{ __html: language['returns.step_3.tax_label'] }}
              />
              <p className={cx('return-summary-return-tax-value', s.summary_value)}>
                {currencyService.formatPrice(returnTax)}
              </p>
              <div className="clearfix" />
            </div>
          )}
        </div>
      )}
      {!hideMethodFees && !hideSummary && (
        <div
          className={cx(
            s.summary_section_container,
            'summary_fee',
            'return-summary-method-container',
          )}
        >
          {Boolean(netCost) && (
            <div>
              <p
                className={cx('return-summary-net-cost-fee-key', s.summary_key)}
                dangerouslySetInnerHTML={{ __html: language['returns.step_3.net_cost_label'] }}
              />
              <p className={cx('return-net-cost-fee-value', s.summary_value)}>
                -{currencyService.formatPrice(netCost)}
              </p>
            </div>
          )}
          {Boolean(discountReversal) && (
            <div>
              <p
                className={cx('return-summary-discount-reversal-key', s.summary_key)}
                dangerouslySetInnerHTML={{
                  __html: language['returns.step_3.discount_reversal_label'],
                }}
              />
              <p className={cx('return-discount-reversal-value', s.summary_value)}>
                -{currencyService.formatPrice(discountReversal)}
              </p>
            </div>
          )}

          <p className={cx('return-summary-method-key', s.summary_key)}>
            <span>{language['returns.step_3.return_method_label']}</span>{' '}
            <span>{methodMessage}</span>
          </p>
          <p className={cx('return-summary-method-value', s.summary_value)}>
            {returnCost != 0 // eslint-disable-line eqeqeq
              ? currencyService.formatPrice(returnCost)
              : freeLabel}
          </p>
          {typeof expressUpgradeFee !== 'undefined' && (
            <div>
              <p
                className={cx('return-summary-upgrade-key', s.summary_key)}
                dangerouslySetInnerHTML={{
                  __html: language['returns.step_3.express_upgrade_fee_label'],
                }}
              />
              <p className={cx('return-summary-upgrade-value', s.summary_value)}>
                {currencyService.formatPrice(expressUpgradeFee)}
              </p>
            </div>
          )}
          {!useHandlingFeeAsRepairCost && Boolean(handlingFee) && (
            <div>
              <p
                className={cx('return-summary-handling-key', s.summary_key)}
                dangerouslySetInnerHTML={{
                  __html: language['returns.step_3.return_handling_fee_label'],
                }}
              />
              <p className={cx('return-handling-fee-value', s.summary_value)}>
                -{currencyService.formatPrice(handlingFee)}
              </p>
            </div>
          )}
          {Boolean(creditReversalAmount) && (
            <div>
              <p
                className={cx('return-summary-reversal-amount-key', s.summary_key)}
                dangerouslySetInnerHTML={{
                  __html: language['returns.step_3.express_reversal-amount_fee_label'],
                }}
              />
              <p className={cx('return-summary-reversal-amount-value', s.summary_value)}>
                -{currencyService.formatPrice(creditReversalAmount)}
              </p>
            </div>
          )}
          <div className="clearfix" />
        </div>
      )}

      <div className={cx(s.estimated_refund_container, 'return-summary-refund-container')}>
        <p
          className={cx('return-summary-refund-key', s.summary_key)}
          dangerouslySetInnerHTML={{
            __html:
              giftOrder && language['returns.step_3.estimated_refund_gift_label']
                ? language['returns.step_3.estimated_refund_gift_label']
                : language['returns.step_3.estimated_refund_label'],
          }}
        />
        <p className={cx('return-summary-refund-value', s.summary_value)}>
          {onlyBlindItems && product === 'returns'
            ? language['returns.general.to_be_determined']
            : currencyService.formatPrice(estimatedRefund)}
        </p>
        <div className="clearfix" />
      </div>
    </>
  );
};

FeeSummaryComponent.defaultProps = {
  methodMessage: undefined,
};

FeeSummaryComponent.propTypes = {
  returnSubtotal: PropTypes.number,
  returnCredit: PropTypes.number,
  restockingFee: PropTypes.number,
  netCost: PropTypes.number,
  recyclingFeeReversal: PropTypes.number,
  returnTax: PropTypes.number,
  returnCost: PropTypes.number,
  discountReversal: PropTypes.number,
  expressUpgradeFee: PropTypes.number,
  handlingFee: PropTypes.number,
  creditReversalAmount: PropTypes.number,
  serviceFee: PropTypes.number,
  methodMessage: PropTypes.string,
  onlyBlindItems: PropTypes.bool.isRequired,
  useHandlingFeeAsRepairCost: PropTypes.bool,
  giftOrder: PropTypes.bool.isRequired,
  refundApiEnabled: PropTypes.bool.isRequired,
  estimatedRefund: PropTypes.number,
  language: PropTypes.object.isRequired,
  freeLabel: PropTypes.string.isRequired,
};

export const FeeSummary = connect(mapStateToProps)(FeeSummaryComponent);
